import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import "./Home.css";
import Carousel from "../components/Carousel";
import ImgEvento from "./assets/img/imagenes home/ImgEvento.jpeg";
import ImgActividades from "./assets/img/imagenes home/ImgActividades.jpeg";
import ImgActividades2 from "./assets/img/imagenes home/ImgActividades2.jpeg";
import ImgActividades3 from "./assets/img/imagenes home/ImgActividades3.jpeg";
import ImgVideos from "./assets/img/imagenes home/ImgVideos.jpeg";
import PersonajesCarousel from "../components/PersonajesCarousel";
import AppCardEvente from "../components/AppCardEvente";
import AppCardActividad from "../components/AppCardActividad";
import AppCardVideo from "../components/AppCardVideo";
import { getHomeData, getConfigPagina, getPlaylistVideos, mergeVideoDescripciones, isApiConfigured } from "../services/api";
import CardCarousel from "../components/CardCarousel";

const defaults = {
  secciones: {
    bienvenida: { titulo: "¡Bienvenido al mundo de Balloon & Ben!", subtitulo: null },
    aventuras:  { titulo: "Aventuras",  subtitulo: "Momentos únicos para compartir, aprender y disfrutar juntos.", textoBoton: "Ver todas las Aventuras" },
    actividades:{ titulo: "Actividades", subtitulo: "¡Manos a la obra! Juega y diviértete mientras aprendes.", textoBoton: "Mira nuestras Actividades" },
    musica:     { titulo: "Música",     subtitulo: "Aprende y explora nuevos temas con historias llenas de diversión y conocimiento.", textoBoton: "Mira nuestra agenda completa" },
    personajes: { titulo: "Personajes", subtitulo: "Conoce a los amigos que te acompañarán en cada aventura.", textoBoton: "Conoce nuestros Personajes" },
  },
};

const defaultEventos = Array(4).fill({ img: ImgEvento, desc: "" });
const defaultActividades = [
  { img: ImgActividades, desc: "" },
  { img: ImgActividades2, desc: "" },
  { img: ImgActividades3, desc: "" },
];
const defaultVideos = Array(3).fill({ thumbnail: ImgVideos, title: "", descripcion: "", videoId: null });

const Home = () => {
  const navigate = useNavigate();
  const [homeData, setHomeData] = useState(null);
  const [musicVideos, setMusicVideos] = useState(defaultVideos);
  const shouldReduceMotion = useReducedMotion();

  const sectionVariants = {
    hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 40 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.2 : 0.6,
        ease: [0.22, 1, 0.36, 1],
        staggerChildren: shouldReduceMotion ? 0 : 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: shouldReduceMotion ? 0 : 24 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: shouldReduceMotion ? 0.2 : 0.5, ease: "easeOut" },
    },
  };

  useEffect(() => {
    if (!isApiConfigured()) return;

    getHomeData().then(setHomeData).catch(() => {});

    getConfigPagina("musica")
      .then((cfg) => Promise.all([
        cfg,
        cfg?.playlist_youtube_id ? getPlaylistVideos(cfg.playlist_youtube_id, 6) : [],
      ]))
      .then(([cfg, videos]) => {
        if (videos.length > 0) setMusicVideos(mergeVideoDescripciones(videos, cfg?.videos_descripciones));
      })
      .catch(() => {});
  }, []);

  const secciones = homeData?.secciones?.reduce((acc, s) => {
    acc[s.seccion] = s;
    return acc;
  }, {}) || {};

  const sec = (key, def) => {
    const s = secciones[key] || defaults.secciones[key] || {};
    return {
      titulo: s.titulo || def.titulo,
      subtitulo: s.subtitulo || def.subtitulo || null,
      descripcionExtra: s.descripcion_extra || null,
      textoBoton: s.texto_boton || def.textoBoton,
    };
  };

  const bie = sec("bienvenida", defaults.secciones.bienvenida);
  const ave = sec("aventuras", defaults.secciones.aventuras);
  const act = sec("actividades", defaults.secciones.actividades);
  const mus = sec("musica", defaults.secciones.musica);
  const per = sec("personajes", defaults.secciones.personajes);

  const eventosApi = homeData?.eventos_destacados || [];
  const actividadesApi = homeData?.actividades_destacadas || [];

  const eventosData = eventosApi.length > 0
    ? eventosApi.map((e) => ({ img: e.imagen || ImgEvento, desc: e.titulo || "", descripcion: e.descripcion || "", eventoId: e.id }))
    : defaultEventos;

  const actividadesData = actividadesApi.length > 0
    ? actividadesApi.map((a) => ({ img: a.imagen || ImgActividades, desc: a.titulo || "", descripcion: a.descripcion || "", actividadId: a.id }))
    : defaultActividades;

  return (
    <div className="home">
      <motion.div
        className="home-wrapper"
        initial="hidden"
        animate="show"
        variants={sectionVariants}
      >
        <motion.h1 className="home-title" variants={itemVariants}>{bie.titulo}</motion.h1>
        {bie.subtitulo && <motion.p className="home-subtitle" variants={itemVariants}>{bie.subtitulo}</motion.p>}
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: shouldReduceMotion ? 0 : 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: shouldReduceMotion ? 0.2 : 0.6, delay: shouldReduceMotion ? 0 : 0.15, ease: "easeOut" }}
      >
        <Carousel />
      </motion.div>

      <motion.div
        className="home-intro"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.3 }}
        variants={itemVariants}
      >
        {bie.descripcionExtra ? (
          <div dangerouslySetInnerHTML={{ __html: bie.descripcionExtra }} />
        ) : (
          <>
            <p>
              Un espacio seguro, creativo y lleno de música donde niños y familias pueden aprender, imaginar y
              compartir juntos. En Balloon and Ben creemos que el entretenimiento infantil también puede ser
              amable, positivo y significativo. Por eso creamos canciones, historias y aventuras que inspiran
              valores como el cuidado del planeta, la empatía, la amistad y el trabajo en equipo.
            </p>
            <p>
              Aquí las familias encuentran un lugar para cantar, bailar, aprender y disfrutar con tranquilidad,
              en un entorno pensado especialmente para los niños. Un mundo donde aprender se convierte en una
              aventura feliz para compartir en familia.
            </p>
          </>
        )}
      </motion.div>

      <motion.section
        className="home-section"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        variants={sectionVariants}
      >
        <motion.h1 className="home-section-title" variants={itemVariants}>{ave.titulo}</motion.h1>
        {ave.subtitulo && <motion.p className="home-section-subtitle" variants={itemVariants}>{ave.subtitulo}</motion.p>}

        <motion.div variants={itemVariants}>
          <CardCarousel
            items={eventosData}
            itemsPerSlide={3}
            renderItem={(evento, j) => (
              <AppCardEvente
                key={j}
                img={evento.img}
                desc={evento.desc}
                descripcion={evento.descripcion || ""}
                onClick={evento.eventoId ? undefined : () => navigate("/aventuras")}
                eventoId={evento.eventoId || undefined}
              />
            )}
          />
        </motion.div>
        <motion.div className="btn-container" variants={itemVariants}>
          <motion.button className="btn-outline" onClick={() => navigate("/aventuras")} whileTap={{ scale: 0.96 }}>{ave.textoBoton}</motion.button>
        </motion.div>
      </motion.section>

      <motion.section
        className="home-section"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        variants={sectionVariants}
      >
        <motion.h1 className="home-section-title" variants={itemVariants}>{act.titulo}</motion.h1>
        {act.subtitulo && <motion.p className="home-section-subtitle" variants={itemVariants}>{act.subtitulo}</motion.p>}

        <motion.div variants={itemVariants}>
          <CardCarousel
            items={actividadesData}
            itemsPerSlide={3}
            renderItem={(actividad, j) => (
              <AppCardActividad
                key={j}
                img={actividad.img}
                desc={actividad.desc}
                descripcion={actividad.descripcion || ""}
                onClick={actividad.actividadId ? undefined : () => navigate("/actividades")}
                actividadId={actividad.actividadId || undefined}
              />
            )}
          />
        </motion.div>
        <motion.div className="btn-container" variants={itemVariants}>
          <motion.button className="btn-outline" onClick={() => navigate("/actividades")} whileTap={{ scale: 0.96 }}>{act.textoBoton}</motion.button>
        </motion.div>
      </motion.section>

      <motion.section
        className="home-section"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        variants={sectionVariants}
      >
        <motion.h1 className="home-section-title" variants={itemVariants}>{mus.titulo}</motion.h1>
        {mus.subtitulo && <motion.p className="home-section-subtitle" variants={itemVariants}>{mus.subtitulo}</motion.p>}

        <motion.div variants={itemVariants}>
          <CardCarousel
            items={musicVideos}
            itemsPerSlide={3}
            renderItem={(video, j) => (
              <AppCardVideo
                key={j}
                thumbnail={video.thumbnail}
                title={video.title}
                descripcion={video.descripcion}
                videoId={video.videoId}
                onClick={() => navigate("/musica")}
              />
            )}
          />
        </motion.div>
        <motion.div className="btn-container" variants={itemVariants}>
          <motion.button className="btn-outline" onClick={() => navigate("/musica")} whileTap={{ scale: 0.96 }}>{mus.textoBoton}</motion.button>
        </motion.div>
      </motion.section>

      <motion.section
        className="home-section"
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, amount: 0.15 }}
        variants={sectionVariants}
      >
        <motion.h1 className="home-section-title" variants={itemVariants}>{per.titulo}</motion.h1>
        {per.subtitulo && <motion.p className="home-section-subtitle" variants={itemVariants}>{per.subtitulo}</motion.p>}

        <motion.div variants={itemVariants}>
          <PersonajesCarousel />
        </motion.div>
        <motion.div className="btn-container" variants={itemVariants}>
          <motion.button className="btn-outline" onClick={() => navigate("/personajes")} whileTap={{ scale: 0.96 }}>{per.textoBoton}</motion.button>
        </motion.div>
      </motion.section>
    </div>
  );
};

export default Home;
